import { useState, useEffect, useCallback, type RefObject } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import type { QuickOption, QuickOptionGroup, TempRange } from '../types';
import { useClickOutside } from './useClickOutside';
import { quickOptions as defaultQuickOptions, quickOptionGroups as defaultQuickOptionGroups } from '../constants/quick-options';
import { parseExpression } from '../utils/expression-parser';

type Tab = 'absolute' | 'relative';

interface UseDateRangePickerProps {
  value?: { from: Dayjs; to: Dayjs };
  onChange?: (range: { from: Dayjs; to: Dayjs }) => void;
  onTimezoneChange?: (timezone: string) => void;
  placeholder: string;
  timezone: string;
  quickOptions?: QuickOption[];
  quickOptionGroups?: QuickOptionGroup[];
  pickerRef: RefObject<HTMLDivElement | null>;
  timezoneRef: RefObject<HTMLDivElement | null>;
}

export function useDateRangePicker({
  value,
  onChange,
  onTimezoneChange,
  placeholder,
  timezone,
  quickOptions,
  quickOptionGroups,
  pickerRef,
  timezoneRef,
}: UseDateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<Tab>('relative');
  const [tempRange, setTempRange] = useState<TempRange>(
    value ?? { from: dayjs().subtract(5, 'day').startOf('day'), to: dayjs().endOf('day') },
  );
  const [calendarMonth, setCalendarMonth] = useState(dayjs());
  const [inputValue, setInputValue] = useState('');
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  const [hoveredDate, setHoveredDate] = useState<Dayjs | null>(null);
  const [selectedQuickLabel, setSelectedQuickLabel] = useState<string | null>(null);

  useEffect(() => {
    if (value) setTempRange(value);
  }, [value]);

  const closeAll = useCallback(() => {
    setIsOpen(false);
    setTimezoneOpen(false);
  }, []);

  useClickOutside([pickerRef, timezoneRef], closeAll);

  const handleQuickSelect = useCallback(
    (option: QuickOption) => {
      const newRange = { from: option.from(), to: option.to() };
      setTempRange(newRange);
      setSelectedQuickLabel(option.label);
      setInputValue(option.display);
      onChange?.(newRange);
      setIsOpen(false);
    },
    [onChange],
  );

  const handleCalendarSelect = useCallback(
    (date: Dayjs) => {
      const newRange = { ...tempRange };

      if (!tempRange.from || (tempRange.from && tempRange.to)) {
        newRange.from = date.startOf('day');
        newRange.to = undefined;
        setInputValue('');
        setSelectedQuickLabel(null);
      } else {
        if (date.isBefore(tempRange.from)) {
          newRange.from = date.startOf('day');
          newRange.to = tempRange.from.endOf('day');
        } else {
          newRange.to = date.endOf('day');
        }

        const fromStr = newRange.from.format('YYYY-MM-DD HH:mm');
        const toStr = newRange.to.format('YYYY-MM-DD HH:mm');
        setInputValue(`${fromStr} to ${toStr}`);
      }

      setTempRange(newRange);

      if (newRange.from && newRange.to) {
        onChange?.({ from: newRange.from, to: newRange.to });
        setIsOpen(false);
      }
    },
    [tempRange, onChange],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setInputValue(val);

      const parsed = parseExpression(val);
      if (parsed) {
        setTempRange({ from: parsed.from, to: parsed.to });
        setSelectedQuickLabel(null);
        onChange?.(parsed);
      }
    },
    [onChange],
  );

  const handleTimezoneChange = useCallback(
    (tz: string) => {
      onTimezoneChange?.(tz);
      setTimezoneOpen(false);
    },
    [onTimezoneChange],
  );

  const formatDisplayValue = useCallback(() => {
    if (selectedQuickLabel) return selectedQuickLabel;
    if (!tempRange.from || !tempRange.to) return placeholder;

    const from = tempRange.from.tz(timezone);
    const to = tempRange.to.tz(timezone);

    return `${from.format('YYYY-MM-DD HH:mm')} — ${to.format('YYYY-MM-DD HH:mm')}`;
  }, [selectedQuickLabel, tempRange, placeholder, timezone]);

  const handleApply = useCallback(() => {
    if (tempRange.from && tempRange.to) {
      setSelectedQuickLabel(null);
      onChange?.({ from: tempRange.from, to: tempRange.to });
      setIsOpen(false);
    }
  }, [tempRange, onChange]);

  return {
    isOpen,
    setIsOpen,
    selectedTab,
    setSelectedTab,
    tempRange,
    setTempRange,
    calendarMonth,
    setCalendarMonth,
    inputValue,
    timezoneOpen,
    setTimezoneOpen,
    hoveredDate,
    setHoveredDate,
    selectedQuickLabel,
    quickOptions: quickOptions ?? defaultQuickOptions,
    quickOptionGroups: quickOptionGroups ?? defaultQuickOptionGroups,
    handleQuickSelect,
    handleCalendarSelect,
    handleInputChange,
    handleTimezoneChange,
    formatDisplayValue,
    handleApply,
  };
}
